import { __rest } from "tslib";
import classNames from 'classnames';
import { get, merge } from 'lodash';
import { LINK_AND_HASHTAG_COLOR, POST_HEADER_THREE_COLOR, POST_HEADER_THREE_FONT, POST_HEADER_TWO_COLOR, POST_HEADER_TWO_FONT, POST_PAGE_FONT, POST_TEXT_COLOR, POST_QUOTES_FONT, POST_TITLE_COLOR, POST_TITLE_FONT, POST_QUOTES_COLOR, IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH, POST_HEADER_FOUR_FONT, POST_HEADER_FOUR_COLOR, POST_HEADER_FIVE_COLOR, POST_HEADER_SIX_COLOR, POST_HEADER_SIX_FONT, POST_HEADER_FIVE_FONT, } from '../../../constants/wix-params';
import { getStyleColor, getPresetColor } from '../../../services/colors';
import { extractFontObjAttrs } from '../../../services/fonts';
import { getStyleObject } from './get-style';
import { createToolbarTheme } from './utils';
import stylesBM from './business-manager-theme.scss';
import styles from './themeRicos.scss';
const presetsFallbacks = {
    h1: 'Title',
    h2: 'Page-title',
    h3: 'Heading-XL',
    h4: 'Heading-L',
    h5: 'Heading-M',
    h6: 'Heading-S',
    quote: 'Body-M',
    p: 'Body-M',
};
const postPresets = {
    h1: [POST_TITLE_FONT, POST_TITLE_COLOR],
    h2: [POST_HEADER_TWO_FONT, POST_HEADER_TWO_COLOR],
    h3: [POST_HEADER_THREE_FONT, POST_HEADER_THREE_COLOR],
    h4: [POST_HEADER_FOUR_FONT, POST_HEADER_FOUR_COLOR],
    h5: [POST_HEADER_FIVE_FONT, POST_HEADER_FIVE_COLOR],
    h6: [POST_HEADER_SIX_FONT, POST_HEADER_SIX_COLOR],
    link: [POST_PAGE_FONT, LINK_AND_HASHTAG_COLOR],
    hashtag: [POST_PAGE_FONT, LINK_AND_HASHTAG_COLOR],
    quote: [POST_QUOTES_FONT, POST_QUOTES_COLOR],
    p: [POST_PAGE_FONT, POST_TEXT_COLOR],
};
const fullPostPresets = (section) => {
    const fullPostFont = (font) => `post-full-${section}-${font}Font`;
    const fullPostColor = (color) => `post-${section}-${color}Color`;
    return Object.assign(Object.assign({}, postPresets), { h1: [fullPostFont('title'), fullPostColor('title')], p: [fullPostFont('description'), fullPostColor('description')], link: [fullPostFont('description'), fullPostColor('linkHashtag')], hashtag: [fullPostFont('description'), fullPostColor('linkHashtag')], quote: [fullPostFont('description'), fullPostColor('description')] });
};
const layoutToWixParams = (_a) => {
    var { appSettings, isMobile, section, isPostPage, isBusinessManager, isEditor, shouldApplyPostDesignInFeed } = _a, rest = __rest(_a, ["appSettings", "isMobile", "section", "isPostPage", "isBusinessManager", "isEditor", "shouldApplyPostDesignInFeed"]);
    const { mobileFontSize, mobileHeaderThreeFontSize, mobileHeaderTwoFontSize, mobileHeaderFourFontSize, mobileHeaderFiveFontSize, mobileHeaderSixFontSize, mobileQuotesFontSize, headerTwoFontSize, headerThreeFontSize, headerFourFontSize, headerFiveFontSize, headerSixFontSize, } = rest;
    const postOrBiz = isPostPage || isBusinessManager || shouldApplyPostDesignInFeed;
    const advancedPostOpts = get(appSettings, IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH, false);
    const useBasicFontSize = !postOrBiz || !advancedPostOpts;
    const fontSizePresets = {
        h2: isMobile
            ? mobileHeaderTwoFontSize
            : useBasicFontSize
                ? headerTwoFontSize
                : undefined,
        h3: isMobile
            ? mobileHeaderThreeFontSize
            : useBasicFontSize
                ? headerThreeFontSize
                : undefined,
        h4: isMobile
            ? mobileHeaderFourFontSize
            : useBasicFontSize
                ? headerFourFontSize
                : undefined,
        h5: isMobile
            ? mobileHeaderFiveFontSize
            : useBasicFontSize
                ? headerFiveFontSize
                : undefined,
        h6: isMobile
            ? mobileHeaderSixFontSize
            : useBasicFontSize
                ? headerSixFontSize
                : undefined,
        quote: isMobile ? mobileQuotesFontSize : postOrBiz ? '24px' : undefined,
        p: isMobile ? mobileFontSize : postOrBiz ? '18px' : undefined, // 18px is taken from .blog-post-page-font
    };
    const getLineHeightPresets = (isFallback) => ({
        p: isFallback && 'normal',
        h2: isFallback && isMobile && '30px',
        h3: isFallback && isMobile && '26px',
        h4: isFallback && isMobile && '20px',
        h5: isFallback && isMobile && '18px',
        h6: isFallback && isMobile && '16px',
    });
    /** Sets values depended on Blog's predefined layouts */
    const getLayoutStyle = (element) => {
        if (isPostPage) {
            switch (element) {
                case 'link':
                case 'hashtag':
                case 'p':
                    return {
                        // taken from app.scss -> .blog-post-page-font | .blog-post-description-font
                        fontStyle: 'inherit',
                        fontWeight: 'inherit',
                        textDecoration: 'inherit',
                    };
                case 'h2':
                case 'h3':
                case 'h4':
                case 'h5':
                case 'h6':
                    if (advancedPostOpts) {
                        const color = postPresets[element][1];
                        return { color: getStyleColor(appSettings, color) };
                    }
                    break;
                default:
                    break;
            }
        }
        else {
            if (!isBusinessManager && !shouldApplyPostDesignInFeed) {
                if (element === 'quote') {
                    // taken from theme-ooi.scss -> .advancedQuoteStylesOff
                    return { fontSize: '24px' };
                }
            }
            else if (element === 'quote') {
                return { lineHeight: 'normal' };
            }
        }
        return {};
    };
    const presets = postOrBiz ? postPresets : fullPostPresets(section);
    const elementMapper = (idx) => Object.entries(presets).reduce((prev, curr) => (Object.assign(Object.assign({}, prev), { [curr[0]]: curr[1][idx] })), {});
    const fontPresets = elementMapper(0);
    const colorPresets = elementMapper(1);
    return {
        fontPresets,
        colorPresets,
        fontSizePresets,
        getLineHeightPresets,
        getLayoutStyle,
    };
};
const correctPx = (value) => typeof value === 'string' ? `${value.replace(/px/g, '')}px` : `${value}px`;
export function createRicosTheme({ appSettings, isMobile, isPostPageEnabled, isBusinessManager, section, isPostPage, parentClass, isEditor, shouldApplyPostDesignInFeed, isRicosStylingSkinEnabled, }) {
    const _a = getStyleObject(appSettings, {
        isPostPageEnabled,
        isBusinessManager,
    }), { textColor, linkHashtagColor, highlightedColor: actionColor, opaqueBackgroundColor: bgColor } = _a, rest = __rest(_a, ["textColor", "linkHashtagColor", "highlightedColor", "opaqueBackgroundColor"]);
    const { fontPresets, fontSizePresets, getLineHeightPresets, colorPresets, getLayoutStyle, } = layoutToWixParams(Object.assign({ appSettings,
        isMobile,
        section,
        isPostPage,
        isBusinessManager,
        isEditor,
        shouldApplyPostDesignInFeed }, rest));
    const getColor = (element, presetFallback) => {
        const isHeading = /^h[2-6]$/.test(element);
        const isLinkHashtag = element === 'link' || element === 'hashtag';
        const color = getStyleColor(appSettings, colorPresets[element]) ||
            (presetFallback && getPresetColor(appSettings, presetFallback)) ||
            (isHeading && '#212121') ||
            (isLinkHashtag && linkHashtagColor);
        return { color };
    };
    /** Font Object from Blog settings */
    const getCustomFontObj = (element) => get(appSettings, `style.fonts.${fontPresets[element]}`);
    const getStyleByRicosElement = (element, ...additionals) => {
        let fallback = false;
        let fontObj = getCustomFontObj(element);
        if (!fontObj) {
            // Fetches fallback from Platform Presets
            fontObj = get(appSettings, `textPresets.${presetsFallbacks[element]}`);
            fallback = true;
        }
        const isBoolean = (val) => typeof val === 'boolean';
        const isLinkOrHashtag = element === 'link' || element === 'hashtag';
        const style = merge(fontObj
            ? Object.assign(Object.assign({}, extractFontObjAttrs(fontObj)), { textDecoration: fontObj.style && isBoolean(fontObj.style.underline)
                    ? fontObj.style.underline
                        ? 'underline'
                        : undefined
                    : fontObj.decoration, fontSize: !isLinkOrHashtag && correctPx(fontObj.size), lineHeight: undefined }) : {}, fallback ? { fontSize: fontSizePresets[element] } : {}, ...additionals);
        if (isLinkOrHashtag) {
            style.fontFamily = undefined;
        }
        return style;
    };
    const bmColor = '#3899ec';
    const fallbackColor = isBusinessManager ? bmColor : undefined;
    const palette = {
        bgColor,
        textColor,
        actionColor,
        fallbackColor,
    };
    const paletteConfig = {
        settingsActionColor: fallbackColor,
        focusActionColor: fallbackColor,
    };
    const blogFontSize = (element) => {
        const fontSize = fontSizePresets[element];
        if (!fontSize) {
            return {};
        }
        return {
            fontSize: typeof fontSize === 'string' ? correctPx(fontSize) : fontSize + 'px',
        };
    };
    const blogLineHeight = (element) => {
        const lineHeight = getLineHeightPresets(!getCustomFontObj(element))[element];
        if (!lineHeight) {
            return {};
        }
        return { lineHeight };
    };
    const customStyles = {
        h1: getStyleByRicosElement('h1', getColor('h1', 'color-5'), getLayoutStyle('h1')),
        h2: getStyleByRicosElement('h2', getColor('h2', 'color-5'), blogFontSize('h2'), blogLineHeight('h2'), getLayoutStyle('h2')),
        h3: getStyleByRicosElement('h3', getColor('h3', 'color-5'), blogFontSize('h3'), blogLineHeight('h3'), getLayoutStyle('h3')),
        h4: getStyleByRicosElement('h4', getColor('h4', 'color-5'), blogFontSize('h4'), blogLineHeight('h4'), getLayoutStyle('h4')),
        h5: getStyleByRicosElement('h5', getColor('h5', 'color-5'), blogFontSize('h5'), blogLineHeight('h5'), getLayoutStyle('h5')),
        h6: getStyleByRicosElement('h6', getColor('h6', 'color-5'), blogFontSize('h6'), blogLineHeight('h6'), getLayoutStyle('h6')),
        link: getStyleByRicosElement('link', getColor('link'), getLayoutStyle('link')),
        hashtag: getStyleByRicosElement('hashtag', getColor('hashtag'), getLayoutStyle('hashtag')),
        quote: getStyleByRicosElement('quote', getColor('quote', 'color-5'), { borderColor: linkHashtagColor }, // linkHashtagColor
        isMobile ? blogFontSize('quote') : {}, blogLineHeight('quote'), getLayoutStyle('quote')),
        p: getStyleByRicosElement('p', getColor('p', 'color-5'), isMobile ? blogFontSize('p') : {}, getLayoutStyle('p')),
    };
    return Object.assign(Object.assign({}, (isRicosStylingSkinEnabled ? {} : { palette, paletteConfig })), { customStyles,
        parentClass });
}
export function getCssOverride({ isViewer, version, isCompact, readOnly: isReadOnly, isBusinessManager, appSettings, } = {}) {
    const { dividerContainer, wrapper, readOnly, sizeCompact, text, orderedListItem, orderedListContainer, unorderedListItem, unorderedListContainer, toolbarButtonWrapper, toolbarButton, footerToolbar, dividerBorderColor, headerTwo, headerTwoFontSize, headerThree, headerThreeFontSize, left, center, right, justify } = styles, rest = __rest(styles, ["dividerContainer", "wrapper", "readOnly", "sizeCompact", "text", "orderedListItem", "orderedListContainer", "unorderedListItem", "unorderedListContainer", "toolbarButtonWrapper", "toolbarButton", "footerToolbar", "dividerBorderColor", "headerTwo", "headerTwoFontSize", "headerThree", "headerThreeFontSize", "left", "center", "right", "justify"]);
    const defaultToolbarTheme = {
        toolbarButton_wrapper: toolbarButtonWrapper,
        toolbarButton,
    };
    const advancedPostOpts = get(appSettings, IS_POST_ADVANCED_DESIGN_OPTIONS_ENABLED_PATH, false);
    const headerTwoStyles = !advancedPostOpts &&
        classNames('blog-post-title-font', headerTwo, headerTwoFontSize);
    const headerThreeStyles = !advancedPostOpts &&
        classNames('blog-post-title-font', headerThree, headerThreeFontSize);
    const theme = Object.assign(Object.assign(Object.assign({}, rest), createToolbarTheme('footer', Object.assign(Object.assign({}, defaultToolbarTheme), { toolbar: classNames(footerToolbar, dividerBorderColor) }))), { headerTwo: headerTwoStyles, headerThreeStyles, wrapper: classNames(wrapper, {
            [readOnly]: isReadOnly,
            [sizeCompact]: isCompact,
        }), readOnly,
        sizeCompact, 'divider-container': isViewer && dividerContainer, text, left: isViewer && classNames(left, text), center: isViewer && classNames(center, text), right: isViewer && classNames(right, text), justify: isViewer && classNames(justify, text), orderedList: classNames(text, /7.16.2/.test(version) && isViewer ? orderedListItem : undefined), orderedListContainer, unorderedList: classNames(text, unorderedListItem), unorderedListContainer });
    if (isBusinessManager) {
        // copied from business-manager-theme.js
        const { input, label, textarea, textInputInputInvalid, imageSettingsSection, galleryImageSettingsSection, imageSettingsLabel, } = stylesBM;
        return Object.assign(Object.assign({}, theme), { textInput_input: input, textInput_input_invalid: textInputInputInvalid, inputWithLabel_input: input, inputWithLabel_textArea: textarea, inputWithLabel_label: label, imageSettingsSection, imageSettings_section: galleryImageSettingsSection, galleryImageSettings_section: galleryImageSettingsSection, layoutsSelector_label: label, layoutsSelector_tile_label: label, radioGroupHorizontal_title: label, sliderWithInput_label: label, imageRatioSelector_label: label, imageRatioSelector_ratioLabel: label, thumbnailPlacementSelector_label: label, imageSettingsLabel });
    }
    return theme;
}
//# sourceMappingURL=themeRicos.js.map